<script setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { computed, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  maxWidth: {
    type: String,
    default: '2xl',
  },
  isSmall: {
    type: Boolean,
    default: true,
  },
  closeable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['close']);

watch(
  () => props.show,
  () => {
    if (props.show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = null;
    }
  }
);

const close = () => {
  if (props.closeable) {
    emit('close');
  }
};

const closeOnEscape = (e) => {
  if (e.key === 'Escape' && props.show) {
    close();
  }
};

onMounted(() => document.addEventListener('keydown', closeOnEscape));

onUnmounted(() => {
  document.removeEventListener('keydown', closeOnEscape);
  document.body.style.overflow = null;
});

const maxWidthClass = computed(() => {
  return {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
    tablet: 'sm:max-w-[720px]',
    full: 'sm:max-w-[95vw]',
  }[props.maxWidth];
});

const isFullScreen = computed(() => props.maxWidth === 'full');
</script>

<template>
  <TransitionRoot as="template" :show="show">
    <Dialog as="div" class="relative z-modal overflow-hidden" v-bind="$attrs" @close="close">
      <TransitionChild
        as="template"
        enter="modal-backdrop-enter"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="modal-backdrop-leave"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 transition-opacity bg-black/75 modal-backdrop"
        />
      </TransitionChild>

      <div
        :class="[maxWidthClass ? maxWidthClass : '']"
        class="fixed inset-0 w-screen mx-auto overflow-y-auto z-100"
      >
        <div class="flex items-center justify-center min-h-full p-4 text-center">
          <TransitionChild
            as="template"
            enter="modal-panel-enter"
            enter-from="opacity-0 translate-y-8 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="modal-panel-leave"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-8 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative w-full overflow-hidden text-left transition-all transform bg-theme-paper rounded-lg shadow-xl modal-panel sm:my-8 md:my-auto sm:w-full"
              :class="{
                'max-w-md': isSmall && !isFullScreen,
                'max-w-7xl': !isSmall && !isFullScreen,
                'max-w-[95vw] max-h-[95vh] h-[95vh]': isFullScreen,
              }"
            >
              <div class="p-4" :class="{ 'h-full': isFullScreen }">
                <slot />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped>
/* Enhanced Modal Animations */

/* Backdrop Animations */
.modal-backdrop-enter {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-backdrop-leave {
  transition: opacity 0.25s cubic-bezier(0.4, 0, 1, 1);
}

/* Panel Animations */
.modal-panel-enter {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-panel-leave {
  transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

/* Respect reduced motion preference */
@media (prefers-reduced-motion: reduce) {
  .modal-backdrop-enter,
  .modal-backdrop-leave,
  .modal-panel-enter,
  .modal-panel-leave {
    transition-duration: 0.01s;
  }
}
</style>
